import { computed, inject, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import {
  orderApiKey,
  type ProductVariantDto,
  type RemoveFromCartRequest,
} from "../../infrastructure/gfo_api/order_api";
import { priceFormatterKey } from "../../infrastructure/price_formatter";
import { currentOrderProviderKey } from "../../infrastructure/current_order_provider";
import ErrorMessage from "../Shared/ErrorMessage";
import { useRouter } from "vue-router";
import { appConfigProviderKey } from "../../infrastructure/app_config_provider";
import { productInfoProviderKey } from "../../infrastructure/productinfo_provider";
import InlineChecker from "../Shared/InlineChecker";
import { ApiValidationError } from "../../infrastructure/gfo_api/api_validation_error";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}
export default {
  template: html,
  components: { ErrorMessage, InlineChecker },
  setup() {
    // Injections
    const { t } = useI18n();
    const orderApi = inject(orderApiKey);
    const currentOrderProvider = inject(currentOrderProviderKey);
    const priceFormatter = inject(priceFormatterKey);
    const router = useRouter();
    const appConfigProvider = inject(appConfigProviderKey);
    const productInfoProvider = inject(productInfoProviderKey);

    // Local vars
    const variants = ref<ProductVariantDto[]>([]);
    const groups = ref<Record<string, number>>({});
    const openGroups = ref<Record<string, boolean>>({});
    const selectedVariants = ref<ProductVariantDto[]>([]);
    const error = ref<string>("");
    const checkerVisible = ref(false);
    const checkerRef = ref<any>(null);

    // computed
    const accessInstallationAddress = computed(() => {
      return currentOrderProvider?.currentOrder?.value?.access_context?.address_label ?? "";
    });
    const hasAccessInstallationAddress = computed(() => accessInstallationAddress.value != "");
    const isSelected = function (variant: ProductVariantDto) {
      return selectedVariants.value.some((v) => v.id == variant.id);
    };
    const saveDisabled = computed(() => selectedVariants.value.length == 0);
    const totalNonRecurring = computed(() => {
      if (vatType.value == "inclusive") {
        return selectedVariants.value.reduce(
          (sum, v) => sum + v.price_fix_incl,
          0,
        );
      } else {
        return selectedVariants.value.reduce((sum, v) => sum + v.price_fix, 0);
      }
    });
    const totalRecurring = computed(() => {
      if (vatType.value == "inclusive") {
        return selectedVariants.value.reduce(
          (sum, v) => sum + v.price_rec_incl,
          0,
        );
      } else {
        return selectedVariants.value.reduce((sum, v) => sum + v.price_rec, 0);
      }
    });
    const vatType = computed(
      () => currentOrderProvider?.currentOrder?.value?.vat_type ?? "inclusive",
    );
    
    const promotions = computed(() => {
      const orderFor = appConfigProvider?.order_for;
      // record productId: array_of_promos
      let res: Record<number, string[]> = {};
      selectedVariants.value.forEach((v) => {
        if ("new" == orderFor){
          res[v.product_id] = v.promotext_new.split("\n");
        } else {
          res[v.product_id] = v.promotext.split("\n");
        }
      })
      return res;
    })

    // methods
    const toggleSelect = function (variant: ProductVariantDto) {
      const alreadySelected = isSelected(variant);
      const newSelection = selectedVariants.value.filter(
        (v) => v.product_id != variant.product_id,
      );
      if (!alreadySelected) {
        newSelection.push(variant);
      }
      selectedVariants.value = newSelection;
      if (!hasAccessInstallationAddress.value && variant.product_id == 1){
        checkerVisible.value = false;
        toggleChecker();
      }
    };
    const toggleChecker = () => {
      checkerVisible.value = !checkerVisible.value;
      if (checkerVisible.value) {
        nextTick(() => {
          const el = checkerRef.value;
          if (!el) return;
          const top = el.getBoundingClientRect().top + window.scrollY - 150;
          window.scrollTo({ top, behavior: "smooth" });
        });
      }
    }
    const checkerHasChecked = async () => {
      await initialize();
      checkerVisible.value = false;
    }

    const variantsByProductId = function (
      productId: number,
    ): ProductVariantDto[] {
      return variants.value.filter((v) => v.product_id == productId);
    };

    const productInfoOpen = function(productId: number){
      productInfoProvider?.openInfoForProductId(productId);
    }
    const productInfosAvailable = function(productId: number): boolean{
      return productInfoProvider?.hasInfoForProductId(productId) ?? false;
    }

    const save = async function () {
      const currentOrder = currentOrderProvider?.currentOrder?.value || null;
      error.value = "";
      try {
        if (!currentOrder) {
          throw new Error("MainProducts_Save_Order_Not_Available");
        }

        for (let i = 0; i < selectedVariants.value.length; i++) {
          if (
            0 ==
            currentOrder.items.filter(
              (item) => item.product_variant_id == selectedVariants.value[i].id,
            ).length
          ) {
            const request = {
              product_variant_id: selectedVariants.value[i].id,
              reference: "",
              quantity: 1,
            };
            const resp =
              (await orderApi?.addToCart(
                currentOrder.order_reference,
                request,
              )) || null;
            if (null != resp) {
                currentOrderProvider?.set(resp.order);
                currentOrderProvider?.setVariants(resp.available_variants);
            }
          }
        }
        // entferne varianten die es nicht mehr gibt
        for (const [_product_title, product_id] of Object.entries(
          groups.value,
        )) {
          if (
            0 ==
            selectedVariants.value.filter((v) => product_id == v.product_id)
              .length
          ) {
            const itemsToRemove =
              currentOrderProvider?.currentOrder?.value?.items.filter(
                (item) => {
                  return item.product_id == product_id;
                },
              ) || [];
            for (let i = 0; i < itemsToRemove.length; i++) {

              const request: RemoveFromCartRequest = {
                product_variant_id: itemsToRemove[i].product_variant_id,
                reference: "",
                quantity: 1,
              };
              const resp =
                (await orderApi?.removeFromCart(
                  currentOrder.order_reference,
                  request,
                )) || null;
              if (null != resp) {
                currentOrderProvider?.set(resp.order);
                currentOrderProvider?.setVariants(resp.available_variants);
              }
            }
          }
          router.push("/options");
        }
      } catch (err) {
        console.log(err)
        if (err instanceof ApiValidationError){
            error.value = err.title;
        } else if (err instanceof Error){
          error.value = err.message;
        } else {
          error.value = String(err);
        }
      }
    };

    const initialize = async function(){
      if (!orderApi) return;
      const currentOrder = currentOrderProvider?.currentOrder?.value;
      const orderReference = currentOrder?.order_reference;
      if (orderReference) {
        const loadedVariants =
          await orderApi.getAvailableVariants(orderReference);
        variants.value = loadedVariants.filter((v) => v.product_is_main);
      }

      let initGroups: Record<string, number> = {};
      let initOpenGroups: Record<string, boolean> = {};
      let initSelection: ProductVariantDto[] = [];
      if (currentOrder) {
        currentOrder.items.forEach((item) => {
          const variant = variants.value.filter(
            (v) => v.id == item.product_variant_id,
          )[0];
          if (variant) {
            initSelection.push(variant);
            initGroups[variant.product_title] = variant.product_id;
            initOpenGroups[variant.product_title] = true;
          }
        });
      } 
      // restliche Gruppen - die momentan nicht in der Bestellung sind
      variants.value
        .sort((a, b) => a.product_position - b.product_position)
        .forEach((v) => {
          if (!(v.product_title in groups)) {
            initGroups[v.product_title] = v.product_id;
          }
        });
      groups.value = initGroups;
      openGroups.value = initOpenGroups;
      selectedVariants.value = initSelection;
      if (!currentOrder){
        checkerVisible.value = true;
      }
    };


    onMounted(async () => {
      await initialize();
    });
    

    return {
      accessInstallationAddress,
      checkerVisible,
      checkerRef,
      checkerHasChecked,
      error,
      groups,
      hasAccessInstallationAddress,
      isSelected,
      openGroups,
      promotions,
      priceFormatter,
      productInfoOpen,
      productInfosAvailable,
      selectedVariants,
      totalNonRecurring,
      totalRecurring,
      vatType,
      variantsByProductId,
      save,
      saveDisabled,
      toggleSelect,
      toggleChecker,
      t,
    };
  },
};
